import React from 'react'
import { Paper, Typography, Box } from '@material-ui/core'
import { VictoryPie } from 'victory'
import { makeStyles } from '@material-ui/core'
import Tooltip from '../../../../../control/tooltip/tooltip'
import { ChartItem } from './DashBoardType'
import useTranslate from '../../../../../i18n/useTranslate'
import clsx from 'clsx'

export type ChartCardProps = {
    title: string
    steps: ChartItem[]
    showChart?: boolean
    chartSize?: number
    columns?: number
    canFilter?: boolean
    onClickLabel?: (item: ChartItem) => void
    onClickProcessingText?: (item: ChartItem) => void
    onClickOverdueText?: (item: ChartItem) => void
}

const COLORS = ['#F56C94', '#FFB75E', '#FFD866', '#55C7BE', '#5DA8FF', '#A47AF6', '#CFCFD6']
const EMPTY_COLOR = '#f3f3f3ff'

const PieChartCard: React.FC<ChartCardProps> = ({
    title,
    steps,
    showChart = true,
    chartSize = 180,
    columns,
    canFilter = true,
    onClickLabel,
    onClickProcessingText,
    onClickOverdueText,
}) => {
    const classes = useChartCardStyles()
    const allZero = steps.length > 0 && steps.every((s) => s.value === 0)
    const translate = useTranslate()
    const isTh = translate.languageStore.currentLanguage === 'th'
    const pieData = allZero
        ? [{ x: 'empty', y: 1 }]
        : steps.map((s) => ({ x: isTh ? (s.nameTh ?? '-') : (s.nameEn ?? '-'), y: s.value }))

    return (
        <Paper className={classes.card} elevation={0}>
            <Typography variant="h6" className={classes.title}>
                {title}
            </Typography>

            <Box className={classes.row}>
                {steps.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" className={classes.empty}>
                        {translate.get('LABEL.NO_DATA_FOUND')}
                    </Typography>
                ) : (
                    <>
                        {showChart && (
                            <Box className={classes.left} style={{ width: chartSize }}>
                                <VictoryPie
                                    width={200}
                                    height={200}
                                    data={pieData}
                                    colorScale={allZero ? [EMPTY_COLOR] : COLORS}
                                    innerRadius={55}
                                    animate={{ duration: 500 }}
                                    labels={() => ''}
                                    radius={100}
                                />
                            </Box>
                        )}

                        <Box
                            className={classes.right}
                            style={{
                                gridTemplateColumns: `repeat(${columns || 2}, auto)`,
                            }}
                        >
                            {steps.map((s, i) => (
                                <Box key={s.key}>
                                    <Box className={classes.legendItem}>
                                        <Box
                                            className={classes.dot}
                                            style={{ background: COLORS[i % COLORS.length] }}
                                        />
                                        <Typography
                                            variant="inherit"
                                            className={clsx(classes.legendText, { [classes.clickable]: canFilter })}
                                            onClick={canFilter ? () => onClickLabel?.(s) : undefined}
                                        >
                                            {isTh ? (s.nameTh ?? '-') : (s.nameEn ?? '-')}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            component="span"
                                            className={clsx(classes.value, { [classes.clickable]: canFilter })}
                                            onClick={canFilter ? () => onClickProcessingText?.(s) : undefined}
                                        >
                                            {(s.value ?? 0).toLocaleString()}
                                        </Typography>
                                        {typeof s.star === 'number' && (
                                            <Tooltip
                                                title={translate.get('UM.PROJECT_DASHBOARD.OVERDUE')}
                                                placement="top"
                                            >
                                                <span>
                                                    <Typography
                                                        variant="body1"
                                                        component="span"
                                                        className={clsx(classes.star, {
                                                            [classes.clickable]: canFilter,
                                                        })}
                                                        onClick={canFilter ? () => onClickOverdueText?.(s) : undefined}
                                                    >
                                                        ({(s.star ?? 0).toLocaleString()}*)
                                                    </Typography>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
            </Box>
        </Paper>
    )
}

export const useChartCardStyles = makeStyles((theme) => ({
    card: {
        borderRadius: 16,
        padding: theme.spacing(2),
        boxShadow: 'none',
        width: '100%',
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        textAlign: 'center',
        marginBottom: theme.spacing(2),
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(3),
        flex: 1,
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    left: {
        flex: '0 0 auto',
        display: 'flex',
        justifyContent: 'center',
    },
    right: {
        flex: 1,
        display: 'grid',
        columnGap: theme.spacing(1),
        rowGap: theme.spacing(1),
        alignContent: 'start',
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    legendItem: {
        display: 'flex',
        marginBottom: 4,
        wordBreak: 'break-word',
    },
    legendText: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
    },
    dot: { width: 10, height: 10, borderRadius: '50%', marginRight: 8, flexShrink: 0, marginTop: 7 },
    value: { marginLeft: 20, fontWeight: 700 },
    star: { marginLeft: 4, color: '#D62E4B', fontWeight: 700 },
    empty: { paddingBlock: 48 },
    clickable: {
        cursor: 'pointer',
    },
}))

export default PieChartCard
